// ФОРМА ЭКРАНА НА САЙТЕ (APP-CLAIMS-1020 ред. 9, §2, §7.1, 7.4.1–7.4.4; закон «экран показывается один раз» —
// CLAUDE.md, docs/HOW-IT-WORKS.md §«Данные на фронте и в приложении»).
//
// Форма — экран без текста и картинок: шапка, секции, виды и высоты строк, места и размеры картинок. Схема одна с
// телефоном — `ShapeSchema` (lib/app-page.ts); здесь только тип: zod в клиентский бандл не тянем.
//
// Откуда берётся форма для заглушки (`<Shape/>`, components/cabinet/skeleton.tsx), по порядку:
//   1) своя с этого устройства — `localStorage` `scriptera:shape:<ключ>|<вид>` (пишет `recordShape` при показе экрана);
//   2) запасная из репо — `components/cabinet/shapes/<маршрут>.json` (снимает водитель маршрутов, зонд
//      `e2e/routes/_shape.js` → команда `shape` водителя);
//   3) запасная сервера — `GET /api/app/pages/shapes` (формы телефона); читается ТОЛЬКО из копии на устройстве:
//      сама ручка зовётся фоном не чаще раза за сессию и в уже показанную заглушку не подставляется;
//   4) нет формы → `null`: заглушка = пустое место (без выдумок).
// Всё синхронно: первый кадр строится сразу, поздний ответ форму показанного экрана не меняет.
import type { AppShape } from './app-page';
import { siteShape } from '@/components/cabinet/shapes';

export type Shape = AppShape;
export type ShapeRow = Shape['sections'][number]['rows'][number];

const LS_PREFIX = 'scriptera:shape:';
const LS_SERVER = 'scriptera:shapes:server';
/** Длинный список — первые 20 строк секции и `more` (§7.1). */
const ROWS_CAP = 20;
/** Строка формы ≤ 2 КБ (§7.1): длиннее — режем строки секций с конца. */
const BYTES_CAP = 2048;

/** Ключ формы `<ключ>|<вид>` — ТО ЖЕ правило, что `shapeKey` в lib/app-page.ts (замок — shape.test.ts). */
export function shapeKeyOf(key: string, view?: Record<string, string>): string {
  const q = Object.entries(view ?? {})
    .filter(([, v]) => v !== '')
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${key}|${q}`;
}

/** Похоже на форму? Лёгкая проверка без zod: то, что лежит на устройстве, могло испортиться. */
export function isShape(raw: unknown): raw is Shape {
  if (!raw || typeof raw !== 'object') return false;
  const s = raw as { key?: unknown; width?: unknown; sections?: unknown };
  if (typeof s.key !== 'string' || !s.key || typeof s.width !== 'number' || !Array.isArray(s.sections)) return false;
  return s.sections.every(
    (sec) =>
      !!sec &&
      typeof sec === 'object' &&
      Array.isArray((sec as { rows?: unknown }).rows) &&
      ((sec as { rows: unknown[] }).rows).every(
        (r) => !!r && typeof r === 'object' && typeof (r as { h?: unknown }).h === 'number' && typeof (r as { kind?: unknown }).kind === 'string',
      ),
  );
}

function lsGet(k: string): unknown {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

/** Запасные формы сервера — копия на устройстве (см. loadServerShapes). */
function serverShapesCopy(): Record<string, unknown> {
  const got = lsGet(LS_SERVER);
  return got && typeof got === 'object' ? (got as Record<string, unknown>) : {};
}

/** Форма экрана для заглушки: устройство → репо → сервер → null. Синхронно. */
export function readShape(key: string, view?: Record<string, string>): Shape | null {
  const id = shapeKeyOf(key, view);
  const own = lsGet(LS_PREFIX + id);
  if (isShape(own)) return own;
  const bundled = siteShape(id);
  if (isShape(bundled)) return bundled;
  const srv = serverShapesCopy()[id];
  return isShape(srv) ? srv : null;
}

/** Записать свою форму экрана на устройство (новая форма того же ключа заменяет прежнюю). */
export function writeShape(shape: Shape): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_PREFIX + shapeKeyOf(shape.key, shape.view), JSON.stringify(shape));
  } catch {
    /* квота / приватный режим — форма опциональна */
  }
}

let serverAsked = false;
/** Один раз за сессию фоном забрать запасные формы сервера в копию на устройстве. Показанное не трогает. */
export function loadServerShapes(): void {
  if (serverAsked || typeof window === 'undefined' || typeof fetch === 'undefined') return;
  serverAsked = true;
  void fetch('/api/app/pages/shapes', { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j: unknown) => {
      const shapes = j && typeof j === 'object' ? (j as { shapes?: unknown }).shapes : null;
      if (!shapes || typeof shapes !== 'object') return;
      try {
        window.localStorage.setItem(LS_SERVER, JSON.stringify(shapes));
      } catch {
        /* квота — не страшно */
      }
    })
    .catch(() => {});
}

// ── Снятие формы по DOM ────────────────────────────────────────────────────────────────────────────────────────
// Одно правило для сайта (recordShape при показе) и для зонда водителя маршрутов (`e2e/routes/_shape.js` зовёт
// `window.__scrShape`). Экран помечает свой корень `data-shape="<маршрут>"`; секции и строки — по разметке:
// явные пометки `data-shape-sec`/`data-shape-row` главнее, иначе — общие классы списков кабинета.

/** Секции списков кабинета. */
const SEC_SEL = '[data-shape-sec], .ai-vgroup, .k-lgroup, .kf-blk, .set-group, .hc-group, .ct-table, .help-list';
/** Строки списков кабинета (карточка бота, ряд переписки/контакта, строка группы, карточка обзора); блоки текста статьи
 *  справки (`.help-body > *`: заголовок, абзац, список, живой пример — заглушка статьи в слое, ред. 11 Д4). */
const ROW_SEL =
  '[data-shape-row], .scn-card, .msg-trow, .scn-trow, .k-lrow, .fk-nlist-row, .ov-scn, .ct-row, .bc-row, .bw-block, .set-row, .hc-row, .help-list > li, .help-body > *';
/** Картинка строки: фото, аватар, плитка. */
const IMG_SEL = 'img, .msg-av, .msg-avph, .scn-card-ava, .ct-ios-av, .fk-nrow-ava, .conn-face, .k-lrow__ico, .fk-nlist-row > .ic, [data-shape-img]';
/** Классы строк из ROW_SEL — по ним вид строки (`kind`), а не по первому общему классу кнопки (`k`). */
const ROW_CLASSES = new Set(Array.from(ROW_SEL.matchAll(/\.([\w-]+)/g), (m) => m[1] ?? ''));

interface Box {
  top: number;
  height: number;
  width: number;
}
const boxOf = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  return { top: r.top, height: r.height, width: r.width };
};
const seen = (el: Element): boolean => {
  const r = el.getBoundingClientRect();
  return r.width > 1 && r.height > 1;
};

/** Сколько строк текста в элементе: высота текстовых узлов / их кегль. */
function linesOf(row: Element): number {
  let n = 0;
  const walker = row.ownerDocument.createTreeWalker(row, 4 /* NodeFilter.SHOW_TEXT */);
  const parents = new Set<Element>();
  for (let t = walker.nextNode(); t; t = walker.nextNode()) {
    if (!t.textContent?.trim() || !t.parentElement) continue;
    parents.add(t.parentElement);
  }
  for (const p of parents) {
    // берём только «листовые» текстовые блоки — у предка с текстом-потомком строки посчитаны у потомка
    if ([...parents].some((q) => q !== p && p.contains(q))) continue;
    const rects = p.getClientRects();
    const tops = new Set<number>();
    for (const r of rects) if (r.height > 1) tops.add(Math.round(r.top));
    n += Math.max(tops.size, seen(p) ? 1 : 0);
  }
  return n;
}

function rowOf(row: Element): ShapeRow {
  const b = boxOf(row);
  const pic = Array.from(row.querySelectorAll(IMG_SEL)).find(seen) ?? null;
  const ib = pic ? boxOf(pic) : null;
  const round = pic ? parseFloat(getComputedStyle(pic).borderTopLeftRadius) >= Math.min(ib!.width, ib!.height) / 2 - 1 : false;
  const kindAttr = row.getAttribute('data-shape-row');
  const classes = Array.from(row.classList);
  const kind = kindAttr || classes.find((c) => ROW_CLASSES.has(c)) || (row.parentElement?.matches('.help-list') ? 'help-item' : classes[0]) || row.tagName.toLowerCase();
  return {
    kind,
    h: Math.round(b.height),
    img: ib ? { w: Math.round(ib.width), h: Math.round(ib.height), ...(round ? { round: true } : {}) } : null,
    lines: linesOf(row),
    chart: !!row.querySelector('svg.chart, .scn-chart, .bw-chart, canvas, [data-shape-chart]'),
  };
}

/** Шапка: вид окна, титул, плашка поиска, полоса папок. */
function headOf(root: Element): Shape['head'] {
  const doc = root.ownerDocument;
  const inSheet = !!root.closest('.drawer, [role="dialog"]');
  const vis = (sel: string) => Array.from(doc.querySelectorAll(sel)).some(seen);
  return {
    mode: inSheet ? 'sheet' : 'root',
    title: vis('.top h1, .drawer h1, .pane-t-row h1'),
    plate: vis('.ai-search, .ai-bar-shell'),
    folders: vis('.msg-folders, .scn-folders, .ct-folders'),
  };
}

/** Форма корня экрана по DOM (§7.1): секции сверху вниз, первые 20 строк каждой, ≤ 2 КБ. */
export function shapeOfElement(root: Element, key: string, view?: Record<string, string>): Shape {
  const secEls = Array.from(root.querySelectorAll(SEC_SEL)).filter((s) => seen(s) && !!s.querySelector(ROW_SEL));
  // Вложенные секции (группа в группе) считаем одной — внешней.
  const tops = secEls.filter((s) => !secEls.some((o) => o !== s && o.contains(s)));
  // Корень сам — группа (таблица «Контактов»): одна секция; иначе строки прямо в корне — секция `root`.
  const groups: Element[] = tops.length > 0 ? tops : [root];
  const rootIsGroup = tops.length === 0 && root.matches(SEC_SEL);
  const sections: Shape['sections'] = groups.map((g, i) => {
    const rowsAll = Array.from(g.querySelectorAll(ROW_SEL)).filter((r) => seen(r));
    // Строка в строке (карточка внутри ряда) — берём внешнюю.
    const rows = rowsAll.filter((r) => !rowsAll.some((o) => o !== r && o.contains(r)));
    const head = g.querySelector('.k-lgroup__title, .ai-vgroup-title, h2, h3');
    return {
      // Секций нет — строки лежат прямо в корне (карточки-пузыри «Ботов»): секция `root`, заглушка рисует их так же.
      id: g === root && !rootIsGroup ? 'root' : g.getAttribute('data-shape-sec') || `s${i}`,
      ...(head && seen(head) ? { title: true } : {}),
      rows: rows.slice(0, ROWS_CAP).map(rowOf),
      ...(rows.length > ROWS_CAP ? { more: rows.length - ROWS_CAP } : {}),
    };
  });
  const shape: Shape = {
    key,
    ...(view && Object.keys(view).length ? { view } : {}),
    width: Math.round(boxOf(root).width) || 1,
    head: headOf(root),
    sections,
  };
  return fitBytes(shape);
}

/** Режем строки с конца самой длинной секции, пока форма не влезет в 2 КБ. */
export function fitBytes(shape: Shape): Shape {
  let cur = shape;
  for (let guard = 0; guard < 200 && JSON.stringify(cur).length > BYTES_CAP; guard++) {
    const idx = cur.sections.reduce((best, s, i, all) => (s.rows.length > (all[best]?.rows.length ?? 0) ? i : best), 0);
    const sec = cur.sections[idx];
    if (!sec || sec.rows.length === 0) break;
    const cut = { ...sec, rows: sec.rows.slice(0, -1), more: (sec.more ?? 0) + 1 };
    cur = { ...cur, sections: cur.sections.map((s, i) => (i === idx ? cut : s)) };
  }
  return cur;
}

/** Снять и записать форму экрана `key` с корня `[data-shape="key"]` (зовётся после первого кадра с данными). */
export function recordShape(key: string, view?: Record<string, string>): Shape | null {
  if (typeof document === 'undefined') return null;
  const root = Array.from(document.querySelectorAll(`[data-shape="${CSS.escape(key)}"]`)).find(seen);
  if (!root) return null;
  const shape = shapeOfElement(root, key, view);
  if (shape.sections.every((s) => s.rows.length === 0)) return null; // пустой экран — не форма
  writeShape(shape);
  return shape;
}

/** Для зонда водителя маршрутов: формы всех видимых экранов `[data-shape]`, а если пометок нет — `main` целиком. */
export function shapesOnScreen(fallbackKey: string): Shape[] {
  if (typeof document === 'undefined') return [];
  const roots = Array.from(document.querySelectorAll('[data-shape]')).filter(seen);
  if (roots.length > 0) return roots.map((r) => shapeOfElement(r, r.getAttribute('data-shape') || fallbackKey));
  const main = document.querySelector('main') ?? document.body;
  return [shapeOfElement(main, fallbackKey)];
}

if (typeof window !== 'undefined') {
  (window as unknown as { __scrShape?: (k: string) => Shape[] }).__scrShape = shapesOnScreen;
}
